import { Fragment, useEffect, useState } from 'react';
import { useHistory } from "react-router-dom"
import { SemiDonughnutGraph } from "../molecules"
import { OverviewUtilizationCard, ChallengesUtilizationCard } from "../atoms"
import { BENEFIT_TYPE_MAP, SERVICE_CARD_DETAILS, formatCardTileLabel } from '../../../constants/overview';
import { OverviewRatingStar, OverviewSectionIcon } from '../../overview/OverviewSectionIcon';
import { handleAppError } from "../../handlers"
import { commaingNumber, computeRating } from "../../../utils"
import { SpinnerDotted } from "../../common/atoms"

const OverviewServiceCard = ({ benefitType = '', contractId, startDate, endDate, totalCustomerCount = 0, classes = '', isRunningContract = false, setUilizationToBenefit }) => {
  const [state, setState] = useState({ loading: true })
  const history = useHistory()
  const service = BENEFIT_TYPE_MAP[benefitType] ?? benefitType
  const details = SERVICE_CARD_DETAILS[service]
  const digitization = sessionStorage.getItem('digitization');
  const digitization_disable = digitization == 'digitization_disable';
  useEffect(() => {
    if (!contractId) return

    setState(prevState => { return { ...prevState, loading: true } })
    details['requestApi']({ contract_id: contractId, start_date: startDate, end_date: endDate },
      (res) => {
        setState({ ...res.data, loading: false })
      },
      (err) => handleAppError(err)
    )
  }, [contractId])

  const getColor = (number) => {
    number = parseInt(number, 10)
    switch (true) {
      case number <= 515: return '#E8432C'
      case number >= 516 && number <= 670: return '#FF9800'
      case number >= 671: return '#14866D'
    }
  }

  const getServiceData = (service) => {
    const { sponsored_appointments = 0, sponsored_spendings = 0,
      sponsored_utilized_employees_count = 0, sponsored_ratings = {}, utilized_employees_count = 0, consultations_count = 0,
      consultations_amount = 0, consultations_ratings = {}, hra_completed_count = 0, current_health_score = 0, optimum_health_score = 0, ready_to_book = 0,
      awaiting_approval = 0, ongoing_requests = 0, completed_requests = 0 } = state

    var title = service?.replace(/_/g, ' ') ?? ''
    var totalEmployeeCount = state?.total_employee_count ?? totalCustomerCount

    switch (service) {
      case 'consultations':
        title = 'Doctor consultation'
        var completed = utilized_employees_count ?? 0
        var availedAppointments = commaingNumber(consultations_count)
        var availedSpendings = '₹' + commaingNumber(parseInt(consultations_amount, 10))
        var ratingsData = computeRating(consultations_ratings) ?? 0
        var utilization = 0
        if (completed > 0) {
          utilization = totalEmployeeCount > 0 ? Math.round((completed * 100) / totalEmployeeCount) : 0
          setUilizationToBenefit(utilization)
        }
        return { completed, availedAppointments, availedSpendings, ratingsData, title, utilization }
      case 'hra':
        title = 'Health Risk Assessment (HRA)'
        var completed = hra_completed_count ?? 0
        var availedAppointments = commaingNumber(current_health_score)
        availedAppointments = <span className="font-semibold" style={{ color: getColor(availedAppointments) }}>{availedAppointments}</span>
        var availedSpendings = commaingNumber(optimum_health_score)
        availedSpendings = <span className="font-semibold" style={{ color: getColor(availedSpendings) }}>{availedSpendings}</span>
        var utilization = 0
        if (completed > 0) {
          utilization = totalEmployeeCount > 0 ? Math.round((completed * 100) / totalEmployeeCount) : 0
          setUilizationToBenefit(utilization)
        }
        return { completed, availedAppointments, availedSpendings, ratingsData, title, utilization }
      case 'pre_employment':
        title = 'Pre employment'
        var completed = completed_requests ?? 0
        var availedAppointments = ready_to_book
        var availedSpendings = awaiting_approval
        return { ongoing_requests, completed, availedAppointments, availedSpendings, title }
      default:
        title = title.charAt(0).toUpperCase() + title.slice(1)
        var completed = sponsored_utilized_employees_count ?? 0
        var availedAppointments = commaingNumber(sponsored_appointments)
        var availedSpendings = '₹' + commaingNumber(parseInt(sponsored_spendings, 10))
        var ratingsData = computeRating(sponsored_ratings) ?? 0
        var utilization = 0
        if (completed > 0) {
          utilization = totalEmployeeCount > 0 ? Math.round((completed * 100) / totalEmployeeCount) : 0
          setUilizationToBenefit(utilization)
        }
        return { completed, availedAppointments, availedSpendings, ratingsData, title, utilization }
    }
  }

  const dataCardsTitles = (details.cardTiles ?? []).map((t) => formatCardTileLabel(t));
  const dataCardsLiners = details['cardLiners'](isRunningContract)
  const serviceData = getServiceData(service)
  const { sponsored = false, loading } = state
  const { completed, availedAppointments, availedSpendings, ratingsData = [0, 0], title = '', ongoing_requests, utilization } = serviceData
  const [avgRating, totalRatings] = ratingsData

  if (![null, undefined].includes(utilization) && completed < 1) {
    return (
      <div
        className={`rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md ${classes}`}
      >
        <div className='flex min-w-0 gap-3'>
          <OverviewSectionIcon name={details.iconKey} />
          <div className='min-w-0'>
            <div className='flex flex-wrap items-center gap-2'>
              <p className='mb-0 max-w-full text-base font-semibold leading-snug text-gray-800'>{title}</p>
              {sponsored && (
                <span className='inline-flex shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700'>
                  Sponsored
                </span>
              )}
            </div>
            <p className='mb-0 mt-1 text-sm leading-relaxed text-gray-600'>This feature has not been utilized</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md ${classes}`}
    >
      <div className='pb-4'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
          <div className='flex min-w-0 flex-1 gap-3'>
            <OverviewSectionIcon name={details.iconKey} className='mt-0.5' />
            <div className='min-w-0'>
              <div className='flex flex-wrap items-center gap-2'>
                <p className='mb-0 max-w-full text-base font-semibold leading-snug text-gray-800'>{title}</p>
                {sponsored && (
                  <span className='inline-flex shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700'>
                    Sponsored
                  </span>
                )}
              </div>
              {!['hra', 'pre_employment'].includes(service) && !!totalRatings && (
                <p className='mb-0 mt-1 flex flex-wrap items-center gap-1 text-xs leading-relaxed text-gray-600'>
                  <OverviewRatingStar />
                  <span className='font-jetbrains-mono text-sm font-semibold tabular-nums text-gray-800'>{avgRating}</span>
                  <span className='text-gray-500'>/5</span>
                  <span className='text-gray-500'>({commaingNumber(totalRatings)} verified ratings)</span>
                </p>
              )}
            </div>
          </div>
          <div className='flex flex-shrink-0 flex-wrap gap-2 lg:justify-end'>
            {details['btns'] &&
              details['btns'].map((item) => (
                <Fragment key={item.text}>
                  {digitization_disable && item?.link === '/test-results' ? null : (
                    <button
                      type='button'
                      className='rounded-lg border border-gray-250 bg-white px-4 py-2 text-xs font-semibold text-gray-800 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2'
                      onClick={() => history.push(item.link)}
                    >
                      {item.text}
                    </button>
                  )}
                </Fragment>
              ))}
          </div>
        </div>
      </div>
      {!loading ?
        <div className='grid grid-cols-3 gap-6'>
          <div className='flex min-h-0 flex-col'>
            {service === 'pre_employment' ? (
              <ChallengesUtilizationCard
                tileLabel={dataCardsTitles[0]}
                title1={ongoing_requests}
                title2={completed}
                liner1='Ongoing checks'
                liner2='Completed checks'
                classes='flex-1 gap-6'
              />
            ) : (
              <SemiDonughnutGraph
                tileLabel={dataCardsTitles[0]}
                linerStyles={completed.toString().length > 5 ? 'w-7/12 text-center bottom-6' : 'bottom-11'}
                id={service + 'myChart'}
                total={state?.total_employee_count ?? totalCustomerCount}
                completed={completed}
                title='employees utilization graph'
                liner={
                  <span>
                    <span className='text-sm font-semibold'>{completed}*</span> out of {state?.total_employee_count ?? totalCustomerCount}{' '}
                    employees
                  </span>
                }
                graphClasses='-mt-8 -mb-4'
                classes='flex-1'
                metricValue={details['donutCardLiner']}
              />
            )}
          </div>
          <div className='flex min-h-0 flex-col'>
            <OverviewUtilizationCard
              tileLabel={dataCardsTitles[1]}
              title={availedAppointments}
              liner={dataCardsLiners[0]}
              classes='flex-1'
              btnText={details['viewDetailsLink'] ? 'View detailed breakup' : ''}
              btnHandler={details['viewDetailsLink'] ? () => history.push(details['viewDetailsLink'], { contractId }) : ''}
            />
          </div>
          <div className='flex min-h-0 flex-col'>
            <OverviewUtilizationCard
              tileLabel={dataCardsTitles[2]}
              title={availedSpendings}
              liner={dataCardsLiners[1]}
              classes='flex-1'
            />
          </div>
        </div> :
        <div className='h-40 flex items-center justify-center'><SpinnerDotted /></div>
      }
    </div>
  )
}

export default OverviewServiceCard